import React from 'react';
import './Projects.css';

function Projects() {
  const projects = [
    {
      title: 'HRMS (Human Resource Management System)',
      type: 'ERP Application',
      description: 'Enterprise resource planning system for employee and project management',
      highlights: [
        'Backend development for employee, project, department, and leave management microservices',
        'Created REST APIs for CRUD operations with JWT Bearer token authentication',
        'Implemented Keycloak integration for complete authorization',
        'Database: PostgreSQL with Hibernate ORM'
      ],
      technologies: ['Java 8', 'Spring Boot', 'JPA', 'Hibernate', 'PostgreSQL', 'Keycloak', 'Swagger'],
      impact: 'Improved employee data management efficiency by 40%',
      gradient: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
      accentColor: '#6366f1'
    },
    {
      title: 'AI-Powered Chatbot',
      type: 'Internal Knowledge System',
      description: 'Intelligent chatbot for semantic search over engineering and platform knowledge',
      highlights: [
        'Built using all-MiniLM embeddings model for semantic understanding',
        'Integrated FAISS vector database for efficient similarity search',
        'Connected with internal platforms using Fast MCP protocol',
        'Enables contextual, tool-based interactions for knowledge retrieval'
      ],
      technologies: ['Python', 'LangChain', 'LangGraph', 'Groq', 'FAISS', 'FastMCP'],
      impact: 'Reduced knowledge lookup time by 60%, improved team productivity',
      gradient: 'linear-gradient(135deg, rgba(34, 211, 238, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%)',
      accentColor: '#22d3ee'
    },
    {
      title: 'ReviseAlgo',
      type: 'Learning Platform',
      description: 'A comprehensive platform where users can learn software engineering and AI engineering concepts through structured roadmaps and interactive content',
      highlights: [
        'Curated learning paths for DSA, system design, and AI engineering',
        'Interactive roadmaps with progress tracking and topic-level notes',
        'AI Engineering curriculum covering LLM foundations, embeddings, and Transformers',
        'Built with Next.js for blazing-fast performance and SEO'
      ],
      technologies: ['Next.js', 'React', 'TypeScript', 'CSS'],
      impact: 'Helping engineers upskill with structured, visual-first learning paths',
      link: 'https://revisealgo.in/',
      gradient: 'linear-gradient(135deg, rgba(52, 211, 153, 0.1) 0%, rgba(34, 211, 238, 0.05) 100%)',
      accentColor: '#34d399'
    },
    {
      title: 'Leaf Disease Classification',
      type: 'ML Project',
      description: 'Image classification system for identifying different types of leaf diseases',
      highlights: [
        'Applied SVM classification algorithm for disease segregation',
        'Implemented image processing for feature extraction',
        'Grouped similar disease patterns based on classification results'
      ],
      technologies: ['Python', 'SVM', 'Image Processing', 'Machine Learning'],
      impact: 'Achieved 85% classification accuracy',
      gradient: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(244, 114, 182, 0.05) 100%)',
      accentColor: '#fbbf24'
    }
  ];

  return (
    <section id="projects" className="projects">
      <div className="container">
        <h2 className="section-title reveal">Featured Projects</h2>
        <p className="section-subtitle reveal">
          A selection of projects that showcase my engineering approach
        </p>
        
        <div className="projects-grid">
          {projects.map((project, index) => (
            <div
              key={index}
              className="project-card reveal"
              style={{
                '--project-accent': project.accentColor,
                background: project.gradient
              }}
            >
              {/* Top accent */}
              <div className="project-accent-line" style={{ background: project.accentColor }} />

              <div className="project-header">
                <div className="project-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={project.accentColor} strokeWidth="2">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                <span className="project-type" style={{ 
                  color: project.accentColor,
                  borderColor: `${project.accentColor}33`
                }}>
                  {project.type}
                </span>
              </div>

              <h3 className="project-title">{project.title}</h3>
              <p className="project-description">{project.description}</p>
              
              <ul className="project-highlights">
                {project.highlights.map((highlight, idx) => (
                  <li key={idx}>
                    <span className="check-icon" style={{ color: project.accentColor }}>✓</span>
                    {highlight}
                  </li>
                ))}
              </ul>
              
              <div className="project-tech">
                {project.technologies.map((tech, idx) => (
                  <span key={idx} className="tech-tag">{tech}</span>
                ))}
              </div>
              
              <div className="project-impact">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={project.accentColor} strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                  <polyline points="17 6 23 6 23 12"></polyline>
                </svg>
                <span><strong>Impact:</strong> {project.impact}</span>
              </div>

              {project.link && (
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-live-link"
                  style={{ '--project-accent': project.accentColor }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                  Visit Live
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Projects;
